import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { Container, Img, EASE } from "@/components/common/Primitives";
import { useLang } from "@/lib/i18n";
import { SIZES, srcSetFor } from "@/lib/images";

/**
 * "Ein dag ved fjorden" – a pinned photo on the left that crossfades as the reader scrolls through
 * the hours of a day on the right. Facts only: times come from the published hours.
 */
const STEPS = [
  { id: "frukost", time: "07.30", img: "/images/restaurant-window.jpg", position: "50% 45%", to: "/mat", nn: ["Frukost med utsikt", "Frukostbuffeen står framme til 10.00. Ta bordet ved vindauget – fjorden ligg rett utanfor.", "Sjå restauranten"], en: ["Breakfast with a view", "The breakfast buffet is out until 10:00. Take the table by the window – the fjord is right outside.", "See the restaurant"] },
  { id: "ut", time: "10.00", img: "/images/kayaks-beach.jpg", position: "50% 55%", to: "/opplev#kayak", nn: ["Ut frå døra", "Kajakkar og syklar står klare, og turkartet frå resepsjonen viser stiane som startar bak bygda.", "Sjå aktivitetane"], en: ["Out the door", "Kayaks and bikes are ready, and the trail map from reception shows the paths that start behind the village.", "See the activities"] },
  { id: "bading", time: "13.00", img: "/images/peparen-dive.jpg", position: "50% 35%", to: "/opplev#peparen", nn: ["Peparen eller bassenget", "Badeøya ligg fem minutt unna. Heime i hagen er bassenget Sjøkanten Trivsel ope juni til august.", "Sjå badeplassane"], en: ["Peparen or the pool", "The bathing island is five minutes away. Back in the garden, the Sjøkanten Trivsel pool is open June to August.", "See where to swim"] },
  { id: "middag", time: "17.00", img: "/images/food-asparagus.jpg", position: "50% 50%", to: "/mat#meny", nn: ["Middag frå kjøkenet", "Maten blir laga frå botnen av, med råvarer frå Etne og Kvinnherad. Søndagar er det søndagsmiddag frå 13.00.", "Sjå menyane"], en: ["Dinner from the kitchen", "Everything is cooked from scratch with produce from Etne and Kvinnherad. Sundays bring Sunday dinner from 13:00.", "See the menus"] },
  { id: "kveld", time: "21.00", img: "/images/sunset-islands.jpg", position: "50% 50%", to: "/mat#bord", nn: ["Kveld ved fjorden", "Fjordterrassen har sol til seint på kvelden om sommaren. Resten av året flyttar me inn i peisestova.", "Bestill bord"], en: ["Evening by the fjord", "The fjord terrace keeps the sun until late on summer evenings. The rest of the year we move inside to the fireplace lounge.", "Book a table"] },
];

export const DayStory = ({ testid = "home-day" }) => {
  const { lang, tx } = useLang();
  const reduce = useReducedMotion();
  const [active, setActive] = useState(0);
  const refs = useRef([]);

  useEffect(() => {
    if (typeof IntersectionObserver === "undefined") return undefined;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => { if (e.isIntersecting) setActive(Number(e.target.dataset.index)); });
      },
      { rootMargin: "-45% 0px -45% 0px", threshold: 0 }
    );
    refs.current.forEach((el) => el && io.observe(el));
    return () => io.disconnect();
  }, []);

  const step = STEPS[active];

  return (
    <section className="py-20 lg:py-32" data-testid={testid} data-active={active}>
      <Container>
        <div className="max-w-2xl">
          <p className="flex items-center gap-3 text-xs font-medium tracking-wide text-muted-foreground"><span className="tabular text-foreground">02</span><span className="h-px w-8 bg-border" aria-hidden />{tx("Ein dag ved fjorden", "A day by the fjord")}</p>
          <h2 className="mt-4 text-[2rem] leading-[1.02] tracking-[-0.03em] text-foreground sm:text-[2.5rem] lg:text-[2.75rem]">{tx("Frå frukost til solnedgang", "From breakfast to sunset")}</h2>
        </div>

        <div className="mt-10 grid grid-cols-1 gap-10 lg:mt-14 lg:grid-cols-12 lg:gap-16">
          {/* Pinned photo (desktop) */}
          <div className="hidden lg:col-span-6 lg:block">
            <div className="sticky top-28">
              <div className="relative aspect-[4/5] overflow-hidden rounded-[28px] bg-muted" data-testid={`${testid}-photo`} data-step={step.id}>
                <AnimatePresence initial={false}>
                  <motion.picture
                    key={step.img}
                    initial={reduce ? false : { opacity: 0, scale: 1.04 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={reduce ? undefined : { opacity: 0 }}
                    transition={{ duration: 0.9, ease: EASE }}
                    className="absolute inset-0 block"
                  >
                    <source type="image/webp" srcSet={srcSetFor(step.img)} sizes={SIZES.half} />
                    <img src={step.img} alt={(lang === "en" ? step.en : step.nn)[0]} className="h-full w-full object-cover" style={{ objectPosition: step.position }} decoding="async" />
                  </motion.picture>
                </AnimatePresence>
                <div className="scrim-hero-soft pointer-events-none absolute inset-x-0 bottom-0 h-[45%]" aria-hidden />
                <div className="absolute inset-x-0 bottom-0 flex items-end justify-between gap-6 p-7">
                  <AnimatePresence mode="wait" initial={false}>
                    <motion.p key={step.id} initial={reduce ? false : { opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={reduce ? undefined : { opacity: 0, y: -8 }} transition={{ duration: 0.35, ease: EASE }} className="hero-shadow text-[2.75rem] font-semibold leading-none tracking-[-0.04em] tabular text-white" data-testid={`${testid}-time`}>
                      {step.time}
                    </motion.p>
                  </AnimatePresence>
                  <div className="flex items-center gap-1.5 pb-2" aria-hidden>
                    {STEPS.map((s, i) => <span key={s.id} className={`h-1 rounded-full transition-[width,background-color] duration-300 ${i === active ? "w-6 bg-white" : "w-1.5 bg-white/45"}`} />)}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Steps */}
          <ol className="lg:col-span-6" data-testid={`${testid}-steps`}>
            {STEPS.map((s, i) => {
              const [title, text, cta] = lang === "en" ? s.en : s.nn;
              const isActive = i === active;
              return (
                <li key={s.id} ref={(el) => { refs.current[i] = el; }} data-index={i} className="border-t border-border py-8 first:border-t-0 first:pt-0 lg:min-h-[52vh] lg:py-10" data-testid={`${testid}-step-${s.id}`} data-active={isActive}>
                  {/* Mobile photo */}
                  <Img src={s.img} alt={title} className="mb-6 aspect-[4/3] lg:hidden" radius="rounded-[22px]" sizes={SIZES.full} position={s.position} />
                  <div className={`transition-opacity duration-500 ${isActive ? "opacity-100" : "lg:opacity-40"}`}>
                    <p className="text-sm font-semibold tabular text-muted-foreground">{s.time}</p>
                    <h3 className="mt-2 text-[1.625rem] leading-tight tracking-[-0.025em] text-foreground sm:text-[1.875rem]">{title}</h3>
                    <p className="mt-3 max-w-[48ch] text-[1.0625rem] leading-relaxed text-muted-foreground">{text}</p>
                    <Link to={s.to} className="group/l mt-4 inline-flex items-center gap-1.5 text-[0.9375rem] font-semibold text-foreground underline decoration-border underline-offset-[6px] transition-colors hover:decoration-foreground" data-testid={`${testid}-link-${s.id}`}>
                      {cta} <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover/l:translate-x-0.5" />
                    </Link>
                  </div>
                </li>
              );
            })}
          </ol>
        </div>
      </Container>
    </section>
  );
};
